import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios_api from '@utils/axios_api';
import { toast } from 'react-toastify';

export function useBizCardUpload() {
    const navigate = useNavigate();

    const [selectedFile,    setSelectedFile]    = useState(null);
    const [previewUrl,      setPreviewUrl]      = useState(null);
    const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });

    // idle | detecting | uploading | processing | done | error
    const [step,            setStep]            = useState('idle');
    const [stepMessage,     setStepMessage]     = useState('');
    const [errorMessage,    setErrorMessage]    = useState('');

    const [useLlm,          setUseLlm]          = useState(true);
    const [cropOverlay,     setCropOverlay]     = useState(null);
    const [cropPreviewUrl,  setCropPreviewUrl]  = useState('');
    const [cropStatusText,  setCropStatusText]  = useState('이미지를 선택하면 자동으로 명함 영역을 탐지합니다.');

    const resetCardState = () => {
        setStep('idle');
        setStepMessage('');
        setErrorMessage('');
        setCropOverlay(null);
        setCropPreviewUrl('');
        setImageNaturalSize({ width: 0, height: 0 });
        setCropStatusText('이미지를 선택하면 자동으로 명함 영역을 탐지합니다.');
    };

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleFileChange = async (file) => {
        if (!file) return;

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        resetCardState();
        setCropStatusText('명함 영역을 자동으로 탐지하는 중...');
        setStep('detecting');

        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios_api.post('/bizscanpro/cards/detect-preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                suppressToast: true,
            });
            const detect  = res.data?.card_detect ?? null;
            const preview = res.data?.crop_preview_data_url || '';
            const points  = Array.isArray(detect?.points) ? detect.points : [];

            setCropStatusText(detect?.method ? `자동 탐지 완료: ${detect.method}` : '탐지 결과를 확인해 주세요.');
            setCropOverlay(
                points.length === 4
                    ? { points, method: detect.method || '-', found: Boolean(detect.found), width: detect.width || 0, height: detect.height || 0 }
                    : null
            );
            setCropPreviewUrl(preview);
        } catch {
            setCropStatusText('자동 탐지를 건너뜁니다. 업로드 후 처리됩니다.');
        } finally {
            setStep('idle');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFileChange(file);
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleUploadAndProcess = async () => {
        if (!selectedFile) {
            toast.warn('이미지를 선택해주세요.');
            return;
        }

        try {
            setStep('uploading');
            setStepMessage('이미지를 서버에 업로드하는 중...');
            setErrorMessage('');

            const formData = new FormData();
            formData.append('file', selectedFile);
            const uploadRes = await axios_api.post('/bizscanpro/cards/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                suppressToast: true,
            });
            const cardId = uploadRes.data?.card_id;
            if (!cardId) throw new Error('카드 ID를 받지 못했습니다. 업로드 응답을 확인해주세요.');

            setStep('processing');
            setStepMessage(
                useLlm
                    ? 'OCR 분석 + AI 언어모델 보정 중... (약 1~3분 소요)'
                    : 'OCR 분석 중...'
            );
            await axios_api.post(
                `/bizscanpro/cards/${cardId}/process`,
                { use_llm: useLlm },
                { suppressToast: true }
            );

            setStep('done');
            setStepMessage('분석 완료! 검수 화면으로 이동합니다...');
            navigate(`/biz/card-review/${cardId}`);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.detail  ||
                err.message                  ||
                '처리 중 오류가 발생했습니다.';
            setStep('error');
            setErrorMessage(msg);
        }
    };

    return {
        selectedFile,
        previewUrl,
        imageNaturalSize,
        setImageNaturalSize,
        step,
        stepMessage,
        errorMessage,
        useLlm,
        setUseLlm,
        cropOverlay,
        cropPreviewUrl,
        cropStatusText,
        handleFileChange,
        handleDrop,
        handleDragOver,
        handleUploadAndProcess,
    };
}
